import { createApiClient } from '@truecare/api-client';

/**
 * Shared API client for the portal.
 *
 * baseUrl is empty so requests like `api.GET('/api/v1/auth/me')` go to
 * the same origin — in dev, Vite proxies /api/* to localhost:3000;
 * in production, app.truecarepartners.ca and api.truecarepartners.ca
 * are deployed together and the portal points at a configured base URL.
 *
 * Audit #17 — on401 is wired here (not deferred to router setup, which never
 * actually happened). Route-guard beforeLoad hooks only catch a 401 on
 * NAVIGATION; a 401 on a mutation or a background refetch (session expired
 * while the user sits on a page) would otherwise surface as a silent error
 * with no redirect. This global handler bounces any non-step-up 401 to
 * sign-in via a full-page navigation (resetting app + query state). The
 * step-up variant of 401 is excluded upstream in the client middleware.
 */
export const api = createApiClient({
  baseUrl: import.meta.env['VITE_API_BASE_URL'] ?? '',
  on401: () => {
    if (typeof window === 'undefined') return;
    // Signed-out / token-consuming surfaces where a 401 from /auth/me is
    // EXPECTED (the visitor is legitimately not signed in) and must NOT bounce
    // to /sign-in — otherwise these pages are unreachable for their only
    // audience. Previously only '/sign-in' was exempt, which silently broke the
    // password-reset flow (both the request page AND the confirm page reached
    // from the email) + the email-change verify page: PortalShell's
    // useCurrentUser fires /auth/me → 401 → this handler → full-page bounce.
    const SIGNED_OUT_SURFACES = ['/sign-in', '/password-reset', '/email-change'];
    const path = window.location.pathname;
    if (SIGNED_OUT_SURFACES.some((s) => path.startsWith(s))) return;
    window.location.assign('/sign-in');
  },
});
